import React, { ChangeEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@components-library/Tooltip';
import { KeyMapKeys } from '@locales/lokalise/keyMap';
import cn from 'classnames';
import { ErrorLabel } from '../errorLabel';
import { Attributes, FormType } from '../formField.interface';
import { BaseOption } from '../forms.types';
import { HintMessage } from '../hintMessage';

type RadioGroupOptionType = BaseOption<string> & {
  title: string;
};

type RadioGroupOptionsType = Array<RadioGroupOptionType>;

type RadioGroupRenderType = JSX.Element | null;

interface IRadioGroupProps {
  name: string;
  title: string;
  options: RadioGroupOptionsType;
  attributes: Attributes;
  getAttributes: (form: FormType) => Attributes;
  className: string;
  valid: boolean;
  validationMessage: string;
  value: string;
  form: FormType;
  hintMessage: string;
  required: boolean;
  renderBefore: RadioGroupRenderType;
  renderAfter: RadioGroupRenderType;
  onChange: ChangeEventHandler<HTMLInputElement>;
  testID?: string;
  tooltip?: string;
}

const RadioGroup = ({
  name,
  title,
  options,
  attributes,
  getAttributes,
  className,
  valid,
  validationMessage,
  value,
  form,
  hintMessage,
  required,
  renderBefore = null,
  renderAfter = null,
  onChange,
  tooltip,
  testID = 'formRadioGroup',
}: IRadioGroupProps) => {
  const { i18n } = useTranslation();
  const attr = getAttributes ? { ...attributes, ...getAttributes(form) } : attributes;

  return (
    <div className={`form__fieldset ${className}`} data-fieldname={name} data-test-id={testID}>
      {renderBefore && <div className="form__field-before">{renderBefore}</div>}

      <div className="form__field form__field--radio">
        {title && (
          <div className="form__field-container">
            <span className={cn('form__label', tooltip && 'form__label_tooltip')}>
              {i18n.t(title as KeyMapKeys)}
              {required && <span className="form__required">*</span>}
              {tooltip && <Tooltip content={tooltip} />}
            </span>
            {!valid && <ErrorLabel message={validationMessage} />}
          </div>
        )}
      </div>
      <div className="form__field-group">
        {options &&
          options.map((o) => (
            <label key={o.value} className={cn('form__radio', { disabled: attr ? attr.disabled : false })}>
              <input
                className={cn('form__radio-input', { invalid: !valid })}
                type="radio"
                name={name}
                checked={o.value === value}
                value={o.value}
                onChange={onChange}
                {...attr}
              />
              <span className="form__radio-label">
                {i18n.t((o.title as KeyMapKeys) || (o.name as KeyMapKeys) || (o.value as KeyMapKeys))}
              </span>
            </label>
          ))}
      </div>

      {hintMessage && <HintMessage valid={valid} message={hintMessage} />}
      {renderAfter && <div className="form__field-after">{renderAfter}</div>}
    </div>
  );
};

export default RadioGroup;
